import { onMounted, onUnmounted, watch } from 'vue';

import type { Ref } from 'vue';

interface BadgingNavigator {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

const BADGE_PREFIX_RE = /^\(\d+\)\s/;

const stripPrefix = (title: string) => title.replace(BADGE_PREFIX_RE, '');

// Badging API rejections are expected outside an installed PWA — swallow them.
const ignoreRejection = () => undefined;

/**
 * Wipe the app-icon badge and the title counter. The badge is origin-wide and
 * outlives the page, so this has to run when the SESSION ends — otherwise one
 * user's count greets the next one on a shared machine.
 *
 * Deliberately NOT tied to `MainLayout` unmounting: an authenticated user
 * landing on an empty-layout route (/access-denied, /server-error, /magic-link,
 * OAuth consent) tears that layout down with their session — and their unread
 * notifications — fully intact.
 */
export const clearAppBadge = (): void => {
  (navigator as BadgingNavigator).clearAppBadge?.()?.catch(ignoreRejection);
  document.title = stripPrefix(document.title);
};

/**
 * Mirrors the inbox unread count onto the installed-PWA app icon (macOS dock /
 * Windows taskbar) via the Badging API, and onto `document.title` as a `(N)`
 * prefix so a plain browser tab gets the same signal. It shows the exact
 * count, not a bare dot — the platform decides how to render it (macOS saturates
 * large numbers to `99+` itself), so the true integer is passed through
 * unformatted.
 *
 * Degrades silently wherever the Badging API is unavailable: Firefox never has
 * it, Chrome/Edge need the app installed, and Safari additionally requires
 * notification permission.
 *
 * `count` is `undefined` until the unread-count query first resolves, and while
 * it is, the badge is left EXACTLY as it was. That matters on a cold start of an
 * installed PWA: the service worker may have stamped a correct count while the
 * app was closed (see `src/service-workers/sw.ts`), and treating "not loaded
 * yet" as zero would blank the dock icon on every launch — or strand it blank
 * for good if the query never resolves. Once a count arrives this composable is
 * the authority and overwrites whatever the worker left.
 */
export const useAppBadge = (count: Ref<number | undefined>) => {
  const badging = navigator as BadgingNavigator;

  let currentCount = 0;
  let titleObserver: MutationObserver | null = null;

  const apply = (value: number | undefined) => {
    if (value === undefined) {
      return;
    }

    currentCount = value;

    const base = stripPrefix(document.title);

    if (value > 0) {
      // `setAppBadge(0)` clears on Chrome but is inconsistent elsewhere — route
      // the empty case through `clearAppBadge()` instead.
      badging.setAppBadge?.(value)?.catch(ignoreRejection);
      document.title = `(${value}) ${base}`;
    } else {
      badging.clearAppBadge?.()?.catch(ignoreRejection);
      document.title = base;
    }
  };

  watch(count, apply, { immediate: true });

  onMounted(() => {
    const title = document.querySelector('title')
      ?? document.head.appendChild(document.createElement('title'));

    // Nearly every page sets its own title through VueUse `useTitle`, which
    // writes <title> wholesale — without this the counter would silently vanish
    // on the first navigation and stay gone until the count next changed.
    // `useTitle` does not observe the DOM by default, so it never reads our
    // write back and there is no loop; the startsWith guard additionally stops
    // our own write from re-triggering the observer.
    //
    // The cost of this scheme: a base title must never legitimately begin with
    // `(N) `, or `stripPrefix` would eat it.
    titleObserver = new MutationObserver(() => {
      if (currentCount <= 0 || document.title.startsWith(`(${currentCount}) `)) {
        return;
      }

      apply(currentCount);
    });

    titleObserver.observe(title, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  });

  // Only the observer is torn down here — clearing the badge is a session-end
  // concern, not a layout one. See `clearAppBadge`.
  onUnmounted(() => {
    titleObserver?.disconnect();
  });
};
